import { clsx } from 'clsx';
import { LogOut, Menu, Moon, Search, Sun, UserRound, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useMatch, useNavigate } from 'react-router';
import { BrandMark } from '../components/layout/BrandMark';
import { Avatar } from '../components/ui/Avatar';
import { IconButton } from '../components/ui/Button';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { Kbd } from '../components/ui/Spinner';
import { useToast } from '../components/ui/Toast';
import { useFocusTrap, useScrollLock } from '../components/ui/overlay';
import { ReminderCenter } from '../features/tasks/ReminderCenter';
import { useAuth } from '../hooks/useAuth';
import { useNavigationHotkeys } from '../hooks/useHotkeys';
import { useMeta } from '../hooks/useMeta';
import { NavSectionContext } from '../hooks/useNavSection';
import { setTheme, useTheme } from '../lib/theme';
import { CommandPalette } from './CommandPalette';
import { FEATURES } from './features';
import { NAV_SECTIONS, type NavItem } from './navigation';

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const MOD_KEY = IS_MAC ? '⌘' : 'Ctrl ';

/** Authenticated app shell: sidebar (drawer below `lg`), top bar with search + user menu, page outlet. */
export function Layout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Set by pages that belong to another section than their URL says (see useNavSection).
  const [activeTo, setActiveTo] = useState<string | null>(null);
  const location = useLocation();
  useNavigationHotkeys();

  // Close the mobile drawer whenever the route changes.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  return (
    <NavSectionContext.Provider value={setActiveTo}>
      <div className="flex min-h-screen bg-canvas">
        <aside className="sticky top-0 hidden h-screen w-60 shrink-0 border-r border-slate-200 bg-slate-100/60 lg:flex">
          <Sidebar activeTo={activeTo} />
        </aside>
        <MobileDrawer open={drawerOpen} activeTo={activeTo} onClose={() => setDrawerOpen(false)} />

        <div className="flex min-w-0 flex-1 flex-col">
          <TopBar onOpenMenu={() => setDrawerOpen(true)} onOpenSearch={() => setPaletteOpen(true)} />
          <main id="main" className="flex min-w-0 flex-1 flex-col">
            <Outlet />
          </main>
        </div>
        <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
        <ReminderCenter />
      </div>
    </NavSectionContext.Provider>
  );
}

function MobileDrawer({ open, activeTo, onClose }: { open: boolean; activeTo: string | null; onClose: () => void }) {
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, open);
  useScrollLock(open);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 lg:hidden">
      <div aria-hidden className="absolute inset-0 animate-fade-in bg-slate-900/40 dark:bg-black/60" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        tabIndex={-1}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        className="absolute inset-y-0 left-0 flex w-64 animate-slide-in-left bg-canvas shadow-panel focus:outline-none"
      >
        <Sidebar activeTo={activeTo} />
        <IconButton icon={X} label="Close navigation" size="sm" onClick={onClose} className="absolute top-3 right-2" data-focus-skip />
      </div>
    </div>
  );
}

/** `activeTo` overrides which link is highlighted; `null` highlights the link matching the URL. */
function Sidebar({ activeTo }: { activeTo: string | null }) {
  const { can } = useAuth();
  const { data: meta } = useMeta();

  return (
    <div className="flex w-full flex-col">
      <div className="flex h-12 items-center gap-2.5 px-4">
        <BrandMark size={24} />
        <div className="min-w-0 leading-tight">
          <div className="text-sm font-semibold tracking-tight text-slate-900">Hearth</div>
          {meta?.settings.companyName && <div className="truncate text-2xs text-slate-500">{meta.settings.companyName}</div>}
        </div>
      </div>
      <nav aria-label="Main" className="flex-1 overflow-y-auto px-2 pt-2 pb-4">
        {NAV_SECTIONS.map((section) => {
          const items = section.items.filter((item) => !item.permission || can(item.permission));
          if (items.length === 0) return null;
          return (
            <div key={section.label ?? 'main'} className="mb-4">
              {section.label && (
                <div className="px-2.5 pb-1 text-2xs font-semibold tracking-wide text-slate-400 uppercase">{section.label}</div>
              )}
              <ul className="flex flex-col gap-px">
                {items.map((item) => (
                  <li key={item.key}>
                    <SidebarLink item={item} activeTo={activeTo} />
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>
    </div>
  );
}

const NO_COUNT = (): number | undefined => undefined;

function SidebarLink({ item, activeTo }: { item: NavItem; activeTo: string | null }) {
  // Each item always calls the same hook, so hook order is stable per rendered link.
  const useCount = FEATURES[item.key].useNavCount ?? NO_COUNT;
  const count = useCount();
  const matchesUrl = useMatch({ path: item.to, end: item.to === '/' }) !== null;
  const isActive = activeTo === null ? matchesUrl : activeTo === item.to;
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      aria-current={isActive ? 'page' : undefined}
      className={clsx(
        'group flex h-8 items-center gap-2.5 rounded-md px-2.5 text-[13px] font-medium transition-colors',
        isActive ? 'bg-surface text-slate-900 shadow-xs ring-1 ring-slate-200' : 'text-slate-600 hover:bg-slate-200/60 hover:text-slate-900',
      )}
    >
      <Icon size={16} aria-hidden className={isActive ? 'text-ember-600' : 'text-slate-400 group-hover:text-slate-600'} />
      <span className="flex-1 truncate">{item.label}</span>
      {count !== undefined && count > 0 && (
        <span className="rounded-full bg-slate-200/80 px-1.5 text-2xs font-semibold text-slate-600 tabular">
          {count > 999 ? '999+' : count}
        </span>
      )}
    </Link>
  );
}

function TopBar({ onOpenMenu, onOpenSearch }: { onOpenMenu: () => void; onOpenSearch: () => void }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const onLogout = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      toast.error(error);
    }
    void navigate('/login', { replace: true });
  }, [logout, navigate, toast]);

  return (
    <header className="sticky top-0 z-30 flex h-12 shrink-0 items-center gap-2 border-b border-slate-200 bg-surface/85 px-3 backdrop-blur sm:px-4">
      <IconButton icon={Menu} label="Open navigation" onClick={onOpenMenu} className="lg:hidden" />
      <button
        type="button"
        onClick={onOpenSearch}
        className="flex h-8 w-full max-w-md min-w-0 items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 text-left text-[13px] text-slate-400 transition-colors hover:border-slate-300 hover:bg-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember-500"
      >
        <Search size={15} aria-hidden className="shrink-0" />
        <span className="flex-1 truncate">Search leads, contacts, notes…</span>
        {/* `max-sm:` rather than `hidden sm:inline-flex`: Kbd's own `inline-flex` would override a plain `hidden`. */}
        <Kbd className="max-sm:hidden">{MOD_KEY}K</Kbd>
      </button>
      <div className="ml-auto flex items-center gap-1">
        <ThemeToggle />
        {user && (
          <DropdownMenu
            align="end"
            aria-label="Account"
            className="min-w-56"
            trigger={(props) => (
              <button
                type="button"
                {...props}
                aria-label={`Account menu for ${user.name}`}
                className="flex h-8 items-center gap-2 rounded-md px-1.5 text-[13px] font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember-500"
              >
                <Avatar name={user.name} size="sm" />
                <span className="hidden max-w-40 truncate md:inline">{user.name}</span>
              </button>
            )}
            items={[
              { type: 'label', key: 'who', label: user.email },
              { key: 'account', label: 'Account', icon: UserRound, onSelect: () => void navigate('/account') },
              { type: 'separator', key: 'sep' },
              { key: 'logout', label: 'Log out', icon: LogOut, onSelect: () => void onLogout() },
            ]}
          />
        )}
      </div>
    </header>
  );
}

function ThemeToggle() {
  const theme = useTheme();
  const next = theme === 'dark' ? 'light' : 'dark';
  return <IconButton icon={theme === 'dark' ? Sun : Moon} label={`Switch to ${next} theme`} onClick={() => setTheme(next)} />;
}
